import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import type { Subscription } from 'rxjs';
import type { RetouchSkippedEffectDto } from '@lite-workspace/shared/dto-retouch';
import { RETOUCH_EVENTS, type RetouchSocketEvent } from '@lite-workspace/shared/retouch/ports';
import { RetouchApi, type RetouchActiveRun, RetouchRuns, type RetouchStatus } from '@lite-workspace/shared/retouch/host';
import { isAutoRetouchedPhoto, isRetouchedPhoto } from '@lite-workspace/dto-fotoclass-photo';
import { ProjectsFacade } from '../features/projects/domain/application/projects.facade';
import { SocketService } from './socket.service';

export type RetouchProcessOutcome = 'done' | 'failed';

type TaggedPhoto = { readonly id: string; readonly tags?: readonly string[] | null };

/** A run of the watched project: every file it was seen with, and how many reported back. */
interface LiveRun {
  files: ReadonlySet<string>;
  done: number;
  failed: number;
  /** Server size of a run restored mid-way: its settled files are not in `files`. */
  total?: number;
}

interface Watched {
  projectId: string;
  subscription: Subscription | null;
  /** Files settled while the `runs/active` read is on the wire: its stale lists must not bring them back. */
  restoring: Set<string> | null;
  /** Bumped by every layers read and socket update: a read answers only if nothing newer came since it went out. */
  layersGen: number;
}

type LayersProgress = { projectId: string; ready: number; total: number };

/** Outcomes kept for a waiter that registers after its events landed (the socket can beat the HTTP answer). */
const SETTLED_KEEP = 50;

/** Root-scoped on purpose: a batch left running in the editor still dims the list tiles on return. */
@Injectable({ providedIn: 'root' })
export class RetouchActivityService {
  private readonly events = inject(RETOUCH_EVENTS, { optional: true });
  private readonly runs = inject(RetouchRuns);
  private readonly facade = inject(ProjectsFacade);
  private readonly router = inject(Router);
  private readonly api = inject(RetouchApi);
  private readonly socket = inject(SocketService);

  private readonly queuedIds = signal<ReadonlySet<string>>(new Set());
  private readonly renderingIds = signal<ReadonlySet<string>>(new Set());
  private readonly skippedByFile = signal<ReadonlyMap<string, readonly RetouchSkippedEffectDto[]>>(new Map());
  // FILE_FAILED is not sent for a file the orchestrator gave up on; FINISHED releases every file of its process.
  private readonly live = signal<ReadonlyMap<string, LiveRun>>(new Map());
  /** New ids of AI rewrites seen landing from «Ждут ретуши»: done before their «retouched» tag arrives. */
  private readonly landedIds = signal<ReadonlySet<string>>(new Set());
  private readonly layers = signal<LayersProgress | null>(null);
  private watched: Watched | null = null;
  private origin: { projectId: string; url: string; selection: readonly string[] | null; starts: number } | null = null;
  private readonly refreshQueued = new Set<string>();
  /** Editor selections by token (`retouch-editor/:projectId?batch=<token>`): the ids never travel in the address.
   *  Memory only — after a reload the token is stale and the guard sends the user back to the list. */
  private readonly batches = new Map<string, { projectId: string; ids: readonly string[] }>();
  private batchSeq = 0;
  private readonly settled = new Map<string, RetouchProcessOutcome>();
  private readonly waiters = new Map<string, ((outcome: RetouchProcessOutcome) => void)[]>();

  /** Files the worker took (FILE_STARTED) and has not reported back. */
  readonly rendering = this.renderingIds.asReadonly();
  /** Files a run holds that the worker has not taken yet: QUEUED by the server or pending in this session's batch. */
  readonly queued = computed<ReadonlySet<string>>(() => {
    const rendering = this.rendering();
    return new Set([...this.queuedIds(), ...this.runs.busy()].filter(id => !rendering.has(id)));
  });

  readonly busy = computed<ReadonlySet<string>>(() => new Set([...this.rendering(), ...this.queued()]));

  /** «Ретушируем N фото»: files the live runs of the project (runs/active + this session) have not reported back;
   *  0 for a project not watched. */
  renderingCount(projectId: string): number {
    if (this.watched?.projectId !== projectId) return 0;
    let count = 0;
    for (const run of this.live().values()) count += Math.max(run.files.size, run.total ?? 0) - run.done - run.failed;
    return count;
  }

  /** «Готовим N из M»: precomputed layers of the project queue; null once all are ready, for an empty queue or a
   *  project not watched. */
  layersProgress(projectId: string): { ready: number; total: number } | null {
    const layers = this.layers();
    return layers?.projectId === projectId ? { ready: layers.ready, total: layers.total } : null;
  }

  /** «Ретушь» page open: the count is read now and again on every socket reconnect (updates lost offline) until the
   *  returned teardown runs. */
  followLayersProgress(projectId: string): () => void {
    this.refreshLayersProgress(projectId);
    const subscription = this.socket.reconnected$.subscribe(() => this.refreshLayersProgress(projectId));
    return () => subscription.unsubscribe();
  }

  /** For the watched project only. A failed read keeps what is shown; a socket update or a later read landing
   *  meanwhile wins over its answer. */
  private refreshLayersProgress(projectId: string): void {
    const entry = this.watched;
    if (entry?.projectId !== projectId) return;
    const gen = ++entry.layersGen;
    this.api.layersProgress(projectId).then(
      progress => {
        if (this.watched === entry && entry.layersGen === gen) this.setLayers(projectId, progress);
      },
      () => undefined,
    );
  }

  /** Tile mark: a file the worker took renders, one a run holds queues; then how the photo was retouched.
   *  Not done: `inScope` (the caller's scope membership) waits, else no mark. */
  markOf(photo: TaggedPhoto, inScope = false): RetouchStatus | null {
    if (this.rendering().has(photo.id)) return 'rendering';
    if (this.queued().has(photo.id)) return 'queued';
    if (!this.isDone(photo)) return inScope ? 'waiting' : null;
    return this.isAi(photo) ? 'ai' : 'hand';
  }

  /** «Готово»: tagged retouched, or an AI rewrite that landed before its tag. */
  isDone(photo: TaggedPhoto): boolean {
    return isRetouchedPhoto(photo) || this.landedIds().has(photo.id);
  }

  /** Of a done photo: retouched by the service, not by hand. */
  isAi(photo: TaggedPhoto): boolean {
    return isAutoRetouchedPhoto(photo) || this.landedIds().has(photo.id);
  }

  /** «Требует ретуши» on the photos: they wait again, a landing seen earlier no longer counts them done. The tag
   *  itself proves nothing — a rewrite may carry its source's tags until the tag task lands. */
  markNeedsRetouch(fileIds: readonly string[]): Promise<void> {
    this.landedIds.update(current => without(current, fileIds));
    return this.facade.markNeedsRetouch(fileIds);
  }

  /** What the last run of this session left untouched in the file; null — this session has not seen a run of it. */
  skippedFor(fileId: string | null | undefined): readonly RetouchSkippedEffectDto[] | null {
    return (fileId && this.skippedByFile().get(fileId)) || null;
  }

  multiPerson(fileId: string | null | undefined): boolean {
    return this.skippedFor(fileId)?.some(item => item.reason === 'multi_person') ?? false;
  }

  /**
   * Follows one project at a time; a repeat for the same project keeps the running subscription. Runs without
   * the `?retouch` switch too: a batch started elsewhere still dims its tiles and leaves its skipped list here.
   * Resolves once the runs already in flight (reload, another device) are restored.
   */
  watch(projectId: string): Promise<void> {
    if (this.watched?.projectId === projectId) return Promise.resolve();
    this.stop();
    if (!this.events) return Promise.resolve();
    const entry: Watched = { projectId, subscription: null, restoring: new Set(), layersGen: 0 };
    this.watched = entry;
    entry.subscription = this.events(projectId).subscribe(event => this.onEvent(entry, event));
    return this.restore(entry);
  }

  /**
   * `fileIds` is what the editor opens; `selection` is what the list had selected at that moment and gets back on
   * return (default: the same ids). They differ when the editor takes a whole filter with nothing selected — the
   * list must then come back with nothing selected, not with the batch. `start` = the photo the editor opens on.
   */
  openEditor(projectId: string, fileIds: readonly string[], selection: readonly string[] = fileIds, start?: string): void {
    if (fileIds.length === 0) return;
    // The section holds its tab in memory: retouched photos = «Готово», reached back through its `?run` switch.
    const path = pathOf(this.router.url);
    const first = start ?? fileIds[0];
    const fromDone =
      path === `/projects/${projectId}/retouch` &&
      this.facade.retouchScope().some(photo => photo.id === first && this.isDone(photo));
    const url = fromDone ? `${path}?run=editor` : this.router.url;
    this.origin = { projectId, url, selection: [...selection], starts: this.runs.starts() };
    const batch = `${Date.now().toString(36)}${(++this.batchSeq).toString(36)}`;
    this.batches.set(batch, { projectId, ids: [...fileIds] });
    void this.router.navigate(['/retouch-editor', projectId], { queryParams: start ? { batch, start } : { batch } });
  }

  /** The single-photo editor on one file; «Назад» lands on `originUrl` (the grid, a lightbox `?photo=<id>`, an editor). */
  openSingle(projectId: string, fileId: string, originUrl: string): void {
    this.origin = { projectId, url: originUrl, selection: null, starts: this.runs.starts() };
    void this.router.navigate(['/retouch-editor', projectId], { queryParams: { files: fileId, single: 1 } });
  }

  /** The selection behind an editor token; null — unknown here (reload, another tab) or of another project. */
  batchIds(projectId: string, batch: string | null | undefined): readonly string[] | null {
    const entry = batch ? this.batches.get(batch) : undefined;
    return entry && entry.projectId === projectId && entry.ids.length > 0 ? entry.ids : null;
  }

  /**
   * Resolves when a process (a revert batch) ends: FINISHED goes out after its last child, so a file event alone
   * never settles it. Any failed child (no backup, worker error) → 'failed', a partial batch included.
   * Without a socket source nothing will ever say, so the HTTP answer is taken as the outcome.
   */
  processOutcome(projectId: string, processId: string): Promise<RetouchProcessOutcome> {
    if (!this.events) return Promise.resolve('done');
    void this.watch(projectId);
    const known = this.settled.get(processId);
    if (known) return Promise.resolve(known);
    return new Promise(resolve => this.waiters.set(processId, [...(this.waiters.get(processId) ?? []), resolve]));
  }

  returnUrl(projectId: string): string {
    return this.origin?.projectId === projectId ? this.origin.url : `/projects/${projectId}/photos`;
  }

  /**
   * The selection the editor was opened with, handed back once to the list the user returns to. Photos the batch
   * rewrote come back under their new ids. Null when the editor was not opened from this very page (same project,
   * same path) or a batch was applied there; any page that asks ends the memory, so it never outlives the first return.
   */
  takeSelection(projectId: string): ReadonlySet<string> | null {
    const origin = this.origin;
    const selection = origin?.selection ?? null;
    if (!origin || !selection) return null;
    origin.selection = null;
    if (origin.projectId !== projectId || pathOf(this.router.url) !== pathOf(origin.url)) return null;
    if (this.runs.starts() !== origin.starts) return null;
    const replaced = this.runs.replacements();
    const follow = (id: string): string => {
      const seen = new Set<string>();
      let current = id;
      while (!seen.has(current)) {
        seen.add(current);
        const next = replaced.get(current);
        if (!next) break;
        current = next;
      }

      return current;
    };

    return new Set(selection.map(follow));
  }

  /** One re-read per tick: the editor's batch end and the FINISHED event land together. */
  refresh(projectId: string): void {
    if (this.refreshQueued.has(projectId)) return;
    this.refreshQueued.add(projectId);
    queueMicrotask(() => {
      this.refreshQueued.delete(projectId);
      void this.facade.refreshPhotoFeed(projectId).catch(() => undefined);
      void this.facade.reloadPhotoCounts(projectId).catch(() => undefined);
      if (this.facade.currentProjectId() === projectId) this.facade.refreshRetouchScope();
    });
  }

  private stop(): void {
    this.watched?.subscription?.unsubscribe();
    this.watched = null;
    this.live.set(new Map());
    this.landedIds.set(new Set());
    this.queuedIds.set(new Set());
    this.renderingIds.set(new Set());
    this.layers.set(null);
  }

  private setLayers(projectId: string, { ready, total }: { ready: number; total: number }): void {
    this.layers.set(total > 0 && ready < total ? { projectId, ready, total } : null);
  }

  /** Seeds the runs the server holds; events that landed during the read win over its lists. */
  private async restore(entry: Watched): Promise<void> {
    let runs: RetouchActiveRun[] = [];
    try {
      runs = await this.api.activeRuns(entry.projectId);
    } catch {
      // No restore: the live events still mark every file from its next step on.
    }

    const settledFiles = entry.restoring ?? new Set<string>();
    entry.restoring = null;
    if (this.watched !== entry) return;
    for (const run of runs) {
      if (this.settled.has(run.processId)) continue;
      const started = run.startedIds.filter(id => !settledFiles.has(id));
      const queued = run.queuedIds.filter(id => !settledFiles.has(id));
      this.live.update(current => {
        const known = current.get(run.processId);
        return new Map(current).set(run.processId, {
          files: new Set([...(known?.files ?? []), ...started, ...queued]),
          done: Math.max(known?.done ?? 0, run.done),
          failed: Math.max(known?.failed ?? 0, run.failed),
          total: run.total,
        });
      });
      this.renderingIds.update(current => new Set([...current, ...started]));
      this.queuedIds.update(current => new Set([...current, ...queued]));
    }
  }

  private onEvent(entry: Watched, event: RetouchSocketEvent): void {
    const { projectId } = entry;
    switch (event.type) {
      case 'RETOUCH_LAYERS_PROGRESS':
        entry.layersGen++;
        this.setLayers(projectId, event);
        return;
      case 'RETOUCH_FILE_QUEUED':
        this.track(event.processId, event.fileId);
        this.queuedIds.update(current => new Set(current).add(event.fileId));
        return;
      case 'RETOUCH_FILE_STARTED':
        this.track(event.processId, event.fileId);
        this.renderingIds.update(current => new Set(current).add(event.fileId));
        return;
      case 'RETOUCH_FILE_DONE':
        this.count(event.processId, 'done');
        this.release([event.fileId]);
        this.adoptLanded(event.fileId, event.newFileId);
        // A revert is no run of RetouchRuns: without this the editor keeps addressing the dead id.
        this.runs.recordReplacement(event.fileId, event.newFileId);
        this.skippedByFile.update(current => {
          const next = new Map(current);
          next.delete(event.fileId);
          return next.set(event.newFileId, event.skipped);
        });
        return;
      case 'RETOUCH_FILE_FAILED':
        this.count(event.processId, 'failed');
        this.release([event.fileId]);
        return;
      case 'RETOUCH_FINISHED':
        this.settle(event.processId, event.failed > 0 ? 'failed' : 'done');
        this.release([...(this.live().get(event.processId)?.files ?? [])]);
        this.live.update(current => {
          const next = new Map(current);
          next.delete(event.processId);
          return next;
        });
        this.refresh(projectId);
        return;
    }
  }

  /** The DONE event beats the «retouched» tag write: a rewrite whose source still waits in the scope is an AI
   *  result. A revert's source is a done photo, so its new id stays out. */
  private adoptLanded(fileId: string, newFileId: string): void {
    const source = this.facade.retouchScope().find(photo => photo.id === fileId);
    if (source && !this.isDone(source)) this.landedIds.update(current => new Set(current).add(newFileId));
  }

  private track(processId: string, fileId: string): void {
    this.live.update(current => {
      const run = current.get(processId) ?? { files: new Set<string>(), done: 0, failed: 0 };
      return new Map(current).set(processId, { ...run, files: new Set(run.files).add(fileId) });
    });
  }

  private count(processId: string, outcome: 'done' | 'failed'): void {
    this.live.update(current => {
      const run = current.get(processId);
      return run ? new Map(current).set(processId, { ...run, [outcome]: run[outcome] + 1 }) : current;
    });
  }

  /** Once per process: a repeated FINISHED changes nothing. */
  private settle(processId: string, outcome: RetouchProcessOutcome): void {
    if (this.settled.has(processId)) return;
    this.settled.set(processId, outcome);
    if (this.settled.size > SETTLED_KEEP) this.settled.delete(this.settled.keys().next().value as string);
    const waiting = this.waiters.get(processId) ?? [];
    this.waiters.delete(processId);
    for (const resolve of waiting) resolve(outcome);
  }

  private release(fileIds: readonly string[]): void {
    if (fileIds.length === 0) return;
    for (const id of fileIds) this.watched?.restoring?.add(id);
    this.queuedIds.update(current => without(current, fileIds));
    this.renderingIds.update(current => without(current, fileIds));
  }
}

function without(current: ReadonlySet<string>, ids: readonly string[]): ReadonlySet<string> {
  const next = new Set(current);
  for (const id of ids) next.delete(id);
  return next;
}

/** The route without its query and fragment: filters in the url do not make it another page. */
function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}
